import { API, PaymentSuccessData } from '@hero/api';
import {
  dateTypeSchema,
  nameSchema,
  objectIdSchema,
  optionalStringSchema,
  Order,
  paginationSchema,
  paginationWithSearchSchema,
  Transaction,
  TransactionStatusSchema,
  TransactionTypeSchema,
} from '@hero/schema';
import z from 'zod';

/**
 * Which transaction date a `simpleDate` filter targets. Mirrors
 * `OrderDateBy` on the order API.
 *
 * - `created` — `Transaction.createdAt`. The default; matches what
 *   the user sees in the transactions list ("recent activity"). Use
 *   for "payments this week", "show me last month's payments", etc.
 * - `approved` — `Transaction.approvedAt`. Only set on transactions
 *   the Finance team approved. Use for "payouts approved this week",
 *   "refunds processed today" — i.e. queries about *when money
 *   actually moved*.
 */
export const transactionDateBySchema = z
  .enum(['created', 'approved'])
  .describe(
    "Which transaction date a `simpleDate` filter targets. 'created' = `Transaction.createdAt` (default; matches 'recent activity'). 'approved' = `Transaction.approvedAt` (only set on Finance-approved tx; use for 'when money actually moved').",
  );
export type TransactionDateBy = z.infer<typeof transactionDateBySchema>;

//* Transaction Unified Categories — groups raw TransactionType into user-friendly filter categories

/** Customer-facing transaction categories */
export const transactionCustomerCategorySchema = z.enum([
  /** customer_invoice_payment, customer_single_order_payment, customer_multi_invoice_payment, customer_pay_all_due_payments, customer_advance_payment */
  'payments',
  /** customer_capture_order_amount */
  'orders',
  /** cashback */
  'cashbacks',
  /** refund */
  'refunds',
]);
export type TransactionCustomerCategory = z.infer<typeof transactionCustomerCategorySchema>;

/** Carrier-facing transaction categories */
export const transactionCarrierCategorySchema = z.enum([
  /** carrier_capture_order_earnings */
  'earnings',
  /** carrier_payout */
  'payouts',
  /** cashback */
  'cashbacks',
  /** refund */
  'refunds',
]);
export type TransactionCarrierCategory = z.infer<typeof transactionCarrierCategorySchema>;

/** Union of all category values (used in query schemas) */
export const transactionCategorySchema = z.union([
  transactionCustomerCategorySchema,
  transactionCarrierCategorySchema,
]);
export type TransactionCategory = z.infer<typeof transactionCategorySchema>;

//* Transaction Query — standalone query schema (same pattern as invoicesQueryAPISchema / ordersQueryAPISchema)
export const transactionsQueryAPISchema = z.object({
  category: transactionCategorySchema
    .optional()
    .describe(
      "Unified transaction category. Customer side: 'payments' | 'orders' | 'cashbacks' | 'refunds'. Carrier side: 'earnings' | 'payouts' | 'cashbacks' | 'refunds'. Backend remaps to the right raw `TransactionType` set per category.",
    ),
  status: TransactionStatusSchema.optional().describe(
    'Raw transaction status (e.g. `pending` / `succeeded` / `failed`). Prefer `category` for high-level filters; use `status` only for fine-grained lifecycle queries.',
  ),
  invoiceId: objectIdSchema
    .optional()
    .describe(
      'Filter all transactions linked to a specific Invoice (`Invoice._id`). Use for "show me the payments on invoice X".',
    ),
  simpleDate: dateTypeSchema
    .optional()
    .describe(
      "Simple date-window filter (today, this_week, this_month, last_week, etc.). Pair with `dateBy` to target a specific date field; defaults to 'created' when `dateBy` is omitted.",
    ),
  dateBy: transactionDateBySchema
    .optional()
    .describe(
      "Which date field `simpleDate` targets. Defaults to 'created' when omitted. See `transactionDateBySchema`.",
    ),
});
export type TransactionsQueryAPI = z.infer<typeof transactionsQueryAPISchema>;

// * get
export const transactionGetOneAPISchema = z.object({
  _id: objectIdSchema.describe('`Transaction._id` (24-char Mongo ObjectId).'),
});
export type TransactionGetOneAPI = API<z.infer<typeof transactionGetOneAPISchema>, Transaction>;

//* API - get current user transactions
export const transactionGetCurrentUserAPISchema = paginationWithSearchSchema.extend({
  ...transactionsQueryAPISchema.shape,
});
export type TransactionGetCurrentUserAPI = API<
  z.infer<typeof transactionGetCurrentUserAPISchema>,
  Transaction[]
>;

// admin apis
export const transactionGetAllAdminAPISchema = paginationWithSearchSchema.extend({
  /** filter by transaction status */
  status: TransactionStatusSchema.optional(),
  /** filter by organization ID */
  orgId: objectIdSchema.optional(),
  /** filter by transaction type */
  type: TransactionTypeSchema.optional(),
  /** filter all transactions by invoice Id */
  invoiceId: objectIdSchema.optional(),
  /** filter all transactions by wallet Id */
  walletId: objectIdSchema.optional(),
});
export type TransactionGetAllAdminAPI = API<
  z.infer<typeof transactionGetAllAdminAPISchema>,
  Transaction[]
>;

//* API - Get orders linked to a transaction
export const transactionOrdersAPISchema = paginationSchema.extend({
  transactionId: objectIdSchema.describe(
    '`Transaction._id` to fetch linked orders for. Use to answer "which orders did this payment cover?".',
  ),
});
export type TransactionOrdersAPI = API<z.infer<typeof transactionOrdersAPISchema>, Order[]>;

export const verifyPendingTransactionAPISchema = z.object({
  /** the transaction id */
  transactionId: objectIdSchema,
  /** the verification type*/
  verification: z.enum(['approve_transaction', 'reject_transaction']),
  /** the reason for the rejection */
  reason: optionalStringSchema(nameSchema),
});
export type VerifyPendingTransactionAPI = API<
  z.infer<typeof verifyPendingTransactionAPISchema>,
  PaymentSuccessData
>;
